import {Button, Dialog, Icon, Loading, Tooltip} from '@startlibs/components'
import {Errors, FormValue, SimpleRadiobox, TextInput, ToggleCheckbox, WithForm} from '@startlibs/form'
import { callIfFunction, getColor } from '@startlibs/utils'
import { darken } from 'polished';
import {useConstant, usePopupToggle, useToggle} from '@startlibs/core'
import React, { Suspense, useEffect, useRef, useState } from 'react'
import _ from 'lodash/fp'
import styled, {css} from 'styled-components'
import {CASE_CLOSED, CASE_REVIEWED, UNDER_REVIEW, WAITING_ACCEPTANCE, WAITING_MORE_INFORMATION} from '../../enums/CaseState';
import {Card, PageContainer, PageFooter, SectionHeading} from '../../components/PageLayout'
import {CaseRequestCard} from '../CaseRequestCard';
import {EmptyListPlaceholder, SearchInput} from '../../components/StyledTable'
import {ExpertProfileCard} from '../experts/components/ExpertProfileCard';
import {ExpertReviewList} from './expertReview/ExpertReviewList'
import {Header} from '../../components/Header'
import {NewExpertShareDialog} from '../NewExpertShareDialog'
import {PaymentConfirmationTag, PaymentSectionHeading} from '../../patient/utils/paymentUtils'
import {PurviewFooter} from '../../components/PurviewFooter';
import {getJwt} from '../../hooks/useJwt'
import {isNotRevoked, isNotRevokedCoReview} from './expertReview/utils'
import {isPaymentPending} from '../../request/utils';
import {jwtGetFetcher, jwtPostFetcher} from '../../utils/authFetch'
import {responseFailure} from '../../utils/validation'
import {useAskVariableExpertMoreInformation} from './hooks/useAskExpertMoreInformation';
import {lazyProviderInfo, lazyUserInfo} from '../../components/WithProvider'
import {willUseSuspense} from '../../hooks/useSuspense'
import {PendingInfo} from './info/PendingInfo'
import {IfLocale} from '../../hocs/IfLocale'
import { FormattedMessage } from 'react-intl';
import { REVIEWER_TYPE_ADMIN, REVIEWER_TYPE_EXPERT } from '../../enums/UserRole';
import { transparentize } from 'polished';
import { PaginationWrapper } from '../experts/ExpertList';
import { Pagination } from '../../components/Pagination';


willUseSuspense(() =>
  jwtGetFetcher(getJwt())(`/api/experts?nocache=${new Date().getTime()}`)
);

const useCurrentExpertsSuspense = willUseSuspense((requestId) =>
  jwtGetFetcher(getJwt())(`/api/experts/bycase/${requestId}`)
)

willUseSuspense((requestId) =>
  jwtGetFetcher(getJwt())(`/api/reviewers?requestId=${requestId}&nocache=${new Date().getTime()}`)
);

export const RecentlyInvited = styled.span`
    color: ${getColor('gray150')};
    font-style: italic;
    font-weight: 400;
`
export const NotProvided = styled(({className}) => <i className={className}><FormattedMessage
  defaultMessage="Not provided"
  description="Not provided box"
/></i>)`
  color: rgba(0,0,0,0.3);
`

const LoadExpertsContainer = styled.div`
    background: white;
    height: 20rem;
    border: 1px solid #d2d2d2;
    border-radius: 6px;
    margin-top: 1.5rem;
`
const ExpertShareHeading = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: space-between;
    p {
      flex-grow: 1;
      margin: .5rem .5rem .5rem 0 ;
    }
    ${SearchInput} {
      flex-basis: 240px;
      flex-shrink: 0;
      margin-right: auto;
    }
    .left-wrapper {
      margin-right: 1rem;
      .flex-wrapper {
        display: flex;
        align-items: center;
        gap: .5rem;
      }
    }
    .right-wrapper {
      ${Button} ~ ${Button} {
        margin-left: .5rem;
      }
    }
`
const GoBackButton = styled(Button)`
    min-width: 7rem;
    margin-right: 1rem;
    ${Icon} {
      font-size: 13px;
    }
`

const ExpertRadioBox = styled(SimpleRadiobox)`
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  > div > div > div {
    position: static;
    > div {
      left: 50%;
      transform: translate(-50%,-50%);
      ${props => props.selected && css`
        background: white;
        border: transparent;
        &:before {
          background: transparent;
        }
      `}
    }
    > span {
      display: none;
    }
  }
`
const SecurityDataWarning = styled.div`
  color: rgba(0,0,0,0.5);
  margin-top: -0.25rem;
  h4 {
    font-size: 12px;
    margin-bottom: 0.25rem;
  }
  p {
    font-size: 11px;
    max-width: 36rem;
  }
`
const EmailConfirmationListContainer = styled.div`
  background-color: rgba(0,0,0,.07);
  border: 1px solid rgba(0,0,0,.1);
  border-radius: 5px;
  margin-bottom: 1rem;
  font-size: 13px;
  padding: 1rem;
  p {
    margin-bottom: 0;
  }
  ul {
    list-style-type: disc;
    margin-top: .5rem;
    margin-left: 2rem;
  }
`
const LoadingContainer = styled.div`
  height: 10rem;
  position: relative;
  background: ${getColor('gray240')};
  border-radius: 6px;
  margin-top: 2rem;
`

const AdminBadge = styled.div`
  background: #e5e5e5;
  color: #3c3c3c;
  padding: 0.5rem 1rem;
  font-size: 12px;
  border-radius: 30px;
  position: absolute;
  right: 120px;
`

const ExpertList = styled.div`
  margin-top: 1.5rem;
  border: 1px solid ${getColor('gray210')};
  border-radius: 5px;
  overflow: hidden;
`
const StyledExpertRow = styled.div`
  cursor: pointer;
  &:nth-child(odd) {
    background-color: ${getColor('backgroundRowColor')};
  }
  &:hover {
    background: ${props => transparentize(0.85, getColor('main')(props))};
  }
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${getColor('gray210')};
  padding: 1rem;
  &:last-child {
    border-bottom: 0px;
  }
  .speciality {
    font-size: 12px;
    opacity: 0.8;
    margin-top: 0.25rem;
  }
  ${props => props.selected && css`
    &:nth-child(odd), &:nth-child(even) {
      background-color: ${getColor('main')};
      color: white;
      &:hover {
        background: ${getColor('main')};
      }
      ${RecentlyInvited}, ${NotProvided} {
        color: white;
      }
    }
    ${Icon} {
      position: absolute;
      left: 50%;
      right: 50%;
      transform: translate(-50%,-50%);
      color: ${getColor('main')};
      width: 10px;
      height: 11px;
      font-weight: 600;
    }
  `}
   ${props => props.highlight && !props.selected && css`
    b {
      color: ${getColor('main')};
    }  
  `}  
`

const ClearSearchButton = styled(Icon)`
  color: ${getColor('gray90')};
  font-size: 12px;
  position: absolute;
  left: auto;
  right: 12px !important;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  width: 1.5rem;
  line-height: 1.5rem;
  text-align: center;
  border-radius: 50%;
  background: rgba(0,0,0,0.1);
  :hover {
    background: #f7d3d4;
    color: ${getColor('alert')};
  }
`

const TabsWrapper = styled.div`
  display: flex;
  padding-bottom: 0rem;
  max-width: 100%;
`

const TabItem = styled.div`
  border: 1px solid ${getColor('gray210')};
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 3rem;
  flex-grow: 1;
  flex-basis: 0;
  width: 0;
  position: relative;
  padding: 0 .5rem;
  .tab-description {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    display: inline-block;
  }
  ${Icon} {
    margin-right: .25rem;
    font-size: 16px;
    vertical-align: top;
  }
  ${props => props.isActive && css`
    border-bottom: 1px solid transparent;
    font-weight: 600;
    background: #FFFFFF;
  `}
  ${props => !props.isActive && css`
    color: ${getColor('gray150')};
  `}
  ${props => props.locked && css`
    opacity: 0.5;
    background: ${getColor('gray240')};
  `}
  ${props => !props.locked && !props.isActive && css`
    cursor: pointer;
  `}
  ${props => props.isChecked && css`
    color: ${getColor('main')};
  `}
  ${props => props.isChecked && !props.isActive && css`
    background: ${getColor('lightGreen')};
  `}
  ${props => !props.isActive && !props.locked && !props.isChecked && css`
    :hover {
      background: rgba(0,0,0,0.05);
    }
  `}
  ${props => !props.isActive && !props.locked && props.isChecked && css`
    :hover {
      background: ${props => darken(0.05, getColor("lightGreen")(props))};
    }
  `}
`

const TabButton = ({label, onClick, isLocked, isActive, isChecked, lockedTooltip}) =>
  <TabItem
    onClick={onClick}
    locked={isLocked}
    isActive={isActive}
    isChecked={isChecked}
  >
    <span className="tab-description">
      {/* {isChecked && <Icon icon='check'/>} */}
      {label}
    </span>
    {
      isLocked && <Tooltip content={lockedTooltip}/>
    }
  </TabItem>

const UNLOADED = []

export const ExpertAssignmentStep = ({caseRequest, setCaseRequest}) => {

  const [currentTab, setCurrentTab] = useState(REVIEWER_TYPE_EXPERT)

  const [rawSharedExperts, setSharedExperts] = useState(useCurrentExpertsSuspense(caseRequest.requestId))
  const sharedExperts = rawSharedExperts.filter(isNotRevoked(caseRequest))
  // const assigning = useToggle(!sharedExperts.length)
  const requestMoreinfo = useAskVariableExpertMoreInformation(caseRequest,setCaseRequest)
  const [rawCoReviewExperts, setActiveCoReviewExperts] = useState(useCurrentExpertsSuspense(caseRequest.requestId))
  const activeCoReviewExperts = rawCoReviewExperts.filter(isNotRevokedCoReview(caseRequest))
  const assigning = useToggle((!(sharedExperts.length || activeCoReviewExperts.length)) && caseRequest.state !== CASE_CLOSED)
  useEffect(() => {
    if (!sharedExperts.length && !assigning.isOpen && !activeCoReviewExperts.length && caseRequest.state !== CASE_CLOSED ) {
      assigning.open()
    }
  }, [sharedExperts.length])

  const [experts, setExperts] = useState(UNLOADED)
  const [admins, setAdmins] = useState(UNLOADED)

  const formRef = useRef()
  const query = useToggle('')
  const queryAdmin = useToggle('')
  const newExpertDialog = useToggle()
  const confirmShareDialog = useToggle()
  const [canAdminReview] = useState(false)
  const providerInfo = lazyProviderInfo.read()
  const userInfo = lazyUserInfo.read()

  const hasNoPaidPayment = !caseRequest.payments.find(payment => payment.paid)
  const hasAnyPendingPayment = caseRequest.payments.find(isPaymentPending)
  const managePaymentsEnabled = providerInfo?.managePaymentsEnabled

  const action = ({selectedExpert, allowDownload, message,sendEmail}) => selectedExpert?.reviewerType === REVIEWER_TYPE_EXPERT 
    ? jwtPostFetcher(getJwt())(`/api/expert/${selectedExpert.id}/share/${caseRequest.requestId}/download/${!!allowDownload}`, {message,sendEmail})
    : jwtPostFetcher(getJwt())(`/api/admin/${selectedExpert.id}/share/${caseRequest.requestId}`, {message,sendEmail})

  // ------ EXPERTS SEARCH AND PARAMS ------
    const [isLoading, setIsLoading] = useState(true)
    const [count, setCount] = useState(0)
    const [totalPages, setTotalPages] = useState(0)

    const [params, rawSetParams] = useState({page: 1, maxResults: 10})
    const debounceSetParams = useConstant(_.debounce(200, rawSetParams))
    
    const setParams = (updater, debounce) => {
      const verifyPage = (updater) => (state) => {
        const updatedState = callIfFunction(updater,state)
        if (
          state.search !== updatedState.search
        ) {
          return _.set('page', 1, updatedState)
        } else if (state.maxResults !== updatedState.maxResults) {
          const firstRow = ((state.maxResults||10) * (updatedState.page-1)+1)
          return _.set('page',Math.max(Math.floor(firstRow/(updatedState.maxResults||10))+1,1),updatedState)
        } else {
          return updatedState
        }
      }
      if (debounce) {
        debounceSetParams(verifyPage(updater))
      } else {
        setTimeout(() => rawSetParams(verifyPage(updater)),0)
      }
      setIsLoading(true)
    }
    
    const maxResults = useToggle(params.maxResults || '')
    usePopupToggle();
    const currentPage = Number(params.page) || 1
        
    usePopupToggle();
    useToggle();
    
    const updateSearch = (v) => {
      query.openWith(v)
      setParams(_.set('search', v), true)
    }
    
    const updateMaxResult = (v) => {
      maxResults.openWith(v)
      setParams(_.set('maxResults', v), true)
    }
    
    const [availableExperts, setAvailableExperts] = useState([])
    
    useEffect(() => {
      setIsLoading(true)
      const URLSafeSearch= params.search ? encodeURIComponent(params.search) : ''
      const maxResults = params.maxResults || 10
      const first = maxResults && params.page ? maxResults * (params.page - 1) : 0
      jwtGetFetcher(getJwt())(`/api/searchExperts?first=${first}&rows=${maxResults}${params.search ? `&search=${URLSafeSearch}` : ''}`)
        .then((response) => {
          setExperts(response.list)
          setTotalPages(Math.ceil((Number(response.count)) / (maxResults)))
          setCount(response.count)
          // setAvailableExperts(response.list)
        })
        .finally(() => setIsLoading(false))
    }, [params])

    useEffect(() => {
      if (activeCoReviewExperts.length > 0){
        setAvailableExperts(
          experts.filter(
            (expert) => activeCoReviewExperts.findIndex((item) => item.expert.id == expert.id ) < 0
          )
        )
      }else{
        setAvailableExperts(experts)
      }
    }, [activeCoReviewExperts.length, experts])

  // ---------- END OF EXPERTS SEARCH ----------

  // ------ EXPERTS SEARCH AND PARAMS ------
  const [isLoadingAdmin, setIsLoadingAdmin] = useState(true)
  const [countAdmin, setCountAdmin] = useState(0)
  const [totalPagesAdmin, setTotalPagesAdmin] = useState(0)

  const [paramsAdmin, rawSetParamsAdmin] = useState({page: 1, maxResults: 10})
  const debounceSetParamsAdmin = useConstant(_.debounce(200, rawSetParamsAdmin))
  
  const setParamsAdmin = (updater, debounce) => {
    const verifyPage = (updater) => (state) => {
      const updatedState = callIfFunction(updater,state)
      if (
        state.search !== updatedState.search
      ) {
        return _.set('page', 1, updatedState)
      } else if (state.maxResults !== updatedState.maxResults) {
        const firstRow = ((state.maxResults||10) * (updatedState.page-1)+1)
        return _.set('page',Math.max(Math.floor(firstRow/(updatedState.maxResults||10))+1,1),updatedState)
      } else {
        return updatedState
      }
    }
    if (debounce) {
      debounceSetParamsAdmin(verifyPage(updater))
    } else {
      setTimeout(() => rawSetParamsAdmin(verifyPage(updater)),0)
    }
    setIsLoadingAdmin(true)
  }
  
  const maxResultsAdmin = useToggle(paramsAdmin.maxResults || '')
  usePopupToggle();
  const currentPageAdmin = Number(paramsAdmin.page) || 1
      
  usePopupToggle();
  useToggle();
  
  const updateSearchAdmin = (v) => {
    queryAdmin.openWith(v)
    setParamsAdmin(_.set('search', v), true)
  }
  
  const updateMaxResultAdmin = (v) => {
    maxResultsAdmin.openWith(v)
    setParamsAdmin(_.set('maxResults', v), true)
  }
  
  const [availableAdmins, setAvailableAdmins] = useState([])
  
  useEffect(() => {
    setIsLoadingAdmin(true)
    const maxResultsAdmin = paramsAdmin.maxResults || 10
    const first = maxResultsAdmin && paramsAdmin.page ? maxResultsAdmin * (paramsAdmin.page - 1) : 0
    const URLSafeSearch= paramsAdmin.search ? encodeURIComponent(paramsAdmin.search) : ''
    jwtGetFetcher(getJwt())(`/api/searchAdminReviewers?requestId=${caseRequest.requestId}&first=${first}&rows=${maxResultsAdmin}${paramsAdmin.search ? `&search=${URLSafeSearch}` : ''}`)
      .then((response) => {
        const currentAdmin = response.list.find((item) => item.id == userInfo.id)
        if (currentAdmin){
          response.list.splice(response.list.indexOf(currentAdmin), 1)
          response.list.unshift(currentAdmin)
        }else{
          if(paramsAdmin.page == 1 && userInfo?.canReviewCases === true){
            const currentAdminFromUserInfo = {
              expertEmail: userInfo.login,
              firstName: userInfo.firstName,
              id: userInfo.id,
              lastName: userInfo.lastName,
              openCases:0,
              picture: userInfo?.picture || '',
              reviewerType: REVIEWER_TYPE_ADMIN,
              specialization: userInfo.specialization
            }
            response.list.unshift(currentAdminFromUserInfo)
          }
        }

        setAdmins(response.list)
        setTotalPagesAdmin(Math.ceil((Number(response.count)) / (maxResultsAdmin)))
        setCountAdmin(response.count)
        // setAvailableExperts(response.list)
      })
      .finally(() => setIsLoadingAdmin(false))
  }, [paramsAdmin])

  useEffect(() => {
    setAvailableAdmins(admins)
  }, [activeCoReviewExperts.length, admins])

// ---------- END OF EXPERTS SEARCH ----------
  
  if (!assigning.isOpen) {
    return <ExpertReviewList
      setCaseRequest={setCaseRequest}
      caseRequest={caseRequest}
      allExperts={experts}
      goToAssign={assigning.open}
      setExperts={setSharedExperts}
      providerInfo={providerInfo}
      activeCoReviewExperts={activeCoReviewExperts}
      setActiveCoReviewExperts={setActiveCoReviewExperts}
      sharedExperts={sharedExperts}
    />
  }

  const selectExpert = (expert) => () => {
    const alreadyShared = sharedExperts.find(share => share.expert.id === expert.id)
    if (alreadyShared) {
      requestMoreinfo(alreadyShared)
    } else {
      confirmShareDialog.open()
    }
  }

  return <>
    <WithForm
      onFailure={responseFailure((n,{status}) => status === 565 && {"case":"This case is shared with another expert"})}
      ref={formRef}
      action={action}
      values={{allowDownload: true,sendEmail: providerInfo.notifications?.assignExpert}}
      onSuccess={({selectedExpert}, {id}) => 
        {
          const selfAssigned = userInfo.canReviewCases && userInfo.id === selectedExpert.id && selectedExpert.reviewerType === REVIEWER_TYPE_ADMIN
          confirmShareDialog.close()
          setCaseRequest(_.flow(
            _.set('state', 
              caseRequest.state === CASE_REVIEWED 
                ? WAITING_MORE_INFORMATION 
                : selfAssigned ? UNDER_REVIEW : WAITING_ACCEPTANCE
              ),
            _.set('activeCaseExpertId', id),
            _.set('activeCaseExpertType', selectedExpert.reviewerType),
            _.set('isAssignedToLoggedUser', selfAssigned)
          ))
          return jwtGetFetcher(getJwt())(`/api/experts/bycase/${caseRequest.requestId}`).then(setSharedExperts).then(assigning.close)
        }
      }
    >{form => <>
     
      {currentTab === REVIEWER_TYPE_EXPERT &&
        <PageContainer>
          <Header title="Expert assignment"></Header>
          <PaymentSectionHeading css="align-items: flex-end;">
            <SectionHeading>
              <p>Select the existing <FormattedMessage defaultMessage="expert" description="expert word"/> you would like to assign this case to, or add a new expert:</p>
            </SectionHeading>
            <IfLocale not contains="LEGAL">
            {(hasAnyPendingPayment || hasNoPaidPayment) && managePaymentsEnabled &&
              <PaymentConfirmationTag pending={hasNoPaidPayment || hasAnyPendingPayment} css="margin-bottom: 1rem;">
                {hasAnyPendingPayment ?
                  <span>There is a payment due. Waiting on confirmation.</span>
                  : <span>No payment has been made.</span>
                }
                <Icon icon="failure"/>
              </PaymentConfirmationTag>
            }
            </IfLocale>
          </PaymentSectionHeading>
          <TabsWrapper>
            <TabButton
              key={REVIEWER_TYPE_EXPERT}
              isLocked={false}
              isChecked={currentTab === REVIEWER_TYPE_EXPERT}
              isActive={currentTab === REVIEWER_TYPE_EXPERT}
              onClick={() => setCurrentTab(REVIEWER_TYPE_EXPERT)}
              label={'Experts'}
            />
            <TabButton
              key={REVIEWER_TYPE_ADMIN}
              isLocked={admins.length === 0 && !isLoadingAdmin && !queryAdmin.isOpen}
              isChecked={currentTab === REVIEWER_TYPE_ADMIN}
              isActive={currentTab === REVIEWER_TYPE_ADMIN}
              onClick={() => {
                if(admins.length > 0 || !isLoadingAdmin || queryAdmin.isOpen){
                  setCurrentTab(REVIEWER_TYPE_ADMIN)
                }
              }}
              label={'Administrators'}
              lockedTooltip={'No administrators with review permissions'}
            />
          </TabsWrapper>
          <Card css="border-radius: 0px 0px 8px 8px; border-top: 0px;">
            <ExpertShareHeading className="heading-actions">
              <div className="left-wrapper">
                <div className="flex-wrapper">
                  {activeCoReviewExperts.length > 0 && <GoBackButton icon="arrow-left" onClick={() => {assigning.close()}}>Back</GoBackButton>}
                  <SearchInput>
                    <Icon icon="search"/>
                    <TextInput placeholder="Search" raw value={query.isOpen} setValue={updateSearch}/>
                    {query.isOpen && <ClearSearchButton icon="x" onClick={() => updateSearch("")} css="left: auto !important; font-size: 12px !important;"/>}
                  </SearchInput>
                  {!isLoading && <div className="query-count">
                      <b>{count} {count === 1 ? "expert" : "experts"}</b>
                  </div>}
                </div>
              </div>
              <div className="right-wrapper">
                <Button
                  disabled={experts === UNLOADED}
                  onClick={_.flow(query.willOpenWith(""), newExpertDialog.open)}
                  icon="plus-circle"
                >Add expert</Button>
              </div>
            </ExpertShareHeading>
            <Suspense fallback={<LoadingContainer><Loading absolute/></LoadingContainer>}>
              <ExpertTable query={query} availableExperts={availableExperts} experts={experts} canAdminReview={canAdminReview} userInfo={userInfo} isLoading={isLoading} currentTab={currentTab}/>
              <PaginationWrapper css="margin-bottom: 1.5rem;">
                <Pagination setPageParam={(page) => () => setParams(_.set('page', page))}
                  currentPage={currentPage}
                  totalPages={totalPages} 
                />
              </PaginationWrapper>
            </Suspense>
            <ToggleCheckbox
              label={<b>Enable expert to download image files from this case</b>}
              path="allowDownload"
              css="margin:2rem 0 0;"
            />
          </Card>
          <PageFooter>
            {
              sharedExperts.length > 0 && <Button onClick={assigning.close}>Cancel</Button>
            }
            <FormValue path="selectedExpert">{expert =>
              <Button
                disabled={!expert}
                highlight
                onClick={selectExpert(expert)}
              >Assign case to selected expert</Button>
            }</FormValue>
          </PageFooter>
        </PageContainer>
      }
      {currentTab === REVIEWER_TYPE_ADMIN &&
        <PageContainer>
          <Header title="Expert assignment"></Header>
          <PaymentSectionHeading css="align-items: flex-end;">
            <SectionHeading>
              <p>Select the existing <FormattedMessage defaultMessage="expert" description="expert word"/> you would like to assign this case to, or add a new expert:</p>
            </SectionHeading>
            <IfLocale not contains="LEGAL">
            {(hasAnyPendingPayment || hasNoPaidPayment) && managePaymentsEnabled &&
              <PaymentConfirmationTag pending={hasNoPaidPayment || hasAnyPendingPayment} css="margin-bottom: 1rem;">
                {hasAnyPendingPayment ?
                  <span>There is a payment due. Waiting on confirmation.</span>
                  : <span>No payment has been made.</span>
                }
                <Icon icon="failure"/>
              </PaymentConfirmationTag>
            }
            </IfLocale>
          </PaymentSectionHeading>
          <TabsWrapper>
            <TabButton
              key={REVIEWER_TYPE_EXPERT}
              isLocked={false}
              isChecked={currentTab === REVIEWER_TYPE_EXPERT}
              isActive={currentTab === REVIEWER_TYPE_EXPERT}
              onClick={() => setCurrentTab(REVIEWER_TYPE_EXPERT)}
              label={'Experts'}
            />
            <TabButton
              key={REVIEWER_TYPE_ADMIN}
              isLocked={false}
              isChecked={currentTab === REVIEWER_TYPE_ADMIN}
              isActive={currentTab === REVIEWER_TYPE_ADMIN}
              onClick={() => setCurrentTab(REVIEWER_TYPE_ADMIN)}
              label={'Administrators'}
            />
          </TabsWrapper>
          <Card css="border-radius: 0px 0px 8px 8px; border-top: 0px;">
            <ExpertShareHeading className="heading-actions">
              <div className="left-wrapper">
                <div className="flex-wrapper">
                  {activeCoReviewExperts.length > 0 && <GoBackButton icon="arrow-left" onClick={() => {assigning.close()}}>Back</GoBackButton>}
                  <SearchInput>
                    <Icon icon="search"/>
                    <TextInput placeholder="Search" raw value={queryAdmin.isOpen} setValue={updateSearchAdmin}/>
                    {queryAdmin.isOpen && <ClearSearchButton icon="x" onClick={() => updateSearchAdmin("")} css="left: auto !important; font-size: 12px !important;"/>}
                  </SearchInput>
                  {!isLoading && <div className="query-count">
                      <b>{countAdmin} {countAdmin === 1 ? "administrator" : "administrators"}</b>
                  </div>}
                </div>
              </div>
            </ExpertShareHeading>
            <Suspense fallback={<LoadingContainer><Loading absolute/></LoadingContainer>}>
              <ExpertTable query={queryAdmin} availableExperts={availableAdmins} experts={admins} canAdminReview={true} userInfo={userInfo} isLoading={isLoadingAdmin} currentTab={currentTab}/>
              <PaginationWrapper css="margin-bottom: 1.5rem;">
                <Pagination setPageParam={(page) => () => setParamsAdmin(_.set('page', page))}
                  currentPage={currentPageAdmin}
                  totalPages={totalPagesAdmin} 
                />
              </PaginationWrapper>
            </Suspense>
            {/* <ToggleCheckbox
              label={<b>Enable expert to download image files from this case</b>}
              path="allowDownload"
              css="margin:2rem 0 0;"
            /> */}
          </Card>
          <PageFooter>
            {
              sharedExperts.length > 0 && <Button onClick={assigning.close}>Cancel</Button>
            }
            <FormValue path="selectedExpert">{expert =>
              <Button
                disabled={!expert}
                highlight
                onClick={selectExpert(expert)}
              >Assign case to selected expert</Button>
            }</FormValue>
          </PageFooter>
        </PageContainer>
      }
      {
        confirmShareDialog.isOpen &&
        <ConfirmShareDialog
          caseRequest={caseRequest} form={form} setExperts={setExperts} hasNoPaidPayment={hasNoPaidPayment}
          hasAnyPendingPayment={hasAnyPendingPayment}
          closeDialog={confirmShareDialog.close} outerForm={form}
          providerInfo={providerInfo}
          userInfo={userInfo}
        />
      }
      <PurviewFooter/>
    </>
    }</WithForm>
    {
      newExpertDialog.isOpen &&
      <NewExpertShareDialog
        setExperts={setExperts}
        closeDialog={newExpertDialog.close}
        selectExpert={(expert) => formRef.current.setValue('selectedExpert', expert)}
      />
    }
  </>
}

const ExpertTable = ({query, availableExperts, experts, canAdminReview, userInfo, isLoading, currentTab}) => {

  if(isLoading){
    return <LoadExpertsContainer >
      <Loading absolute size={40} borderWidth={6}/>
    </LoadExpertsContainer>
  }

  return availableExperts.length > 0 && experts.length > 0 ?
    <ExpertList>
      {
        availableExperts.map((expert, _index) => {
          return <FormValue path="selectedExpert">{(selectedExpert, setSelectedExpert) =>
            <ExpertRow
              selected={selectedExpert?.id === expert.id} setSelectedExpert={setSelectedExpert} selectedExpert={selectedExpert}
              expert={expert} highlight={canAdminReview && expert.reviewerType === REVIEWER_TYPE_ADMIN && expert.id === userInfo.id}
            />
          }</FormValue>
        }
        )
      }
    </ExpertList>
    : 
    
    <>
      {currentTab === REVIEWER_TYPE_EXPERT &&
        <EmptyListPlaceholder>
          {experts.length === 0 && !query.isOpen 
            ? <span>There are no experts registered in this system yet.</span>
            : <span>There are no experts matching your search criteria.</span>
          }
        </EmptyListPlaceholder>
      }
      {currentTab === REVIEWER_TYPE_ADMIN &&
        <EmptyListPlaceholder>
          {experts.length === 0 && !query.isOpen 
            ? <span>There are no administrators with review permissions in this system yet.</span>
            : <span>There are no administrators matching your search criteria.</span>
          }
        </EmptyListPlaceholder>
      }
    </>
}

const ConfirmShareDialog = ({closeDialog, form, caseRequest, providerInfo, userInfo}) => {

  const isSelfAssignment = form.getValue("selectedExpert")?.reviewerType === REVIEWER_TYPE_ADMIN && form.getValue("selectedExpert")?.id === userInfo.id
  if(isSelfAssignment) {
    form.setValue("sendEmail", false)
  }

  return <Dialog
    title="Assign case"
    closeDialog={closeDialog}
    isLoading={false}
    footer={<>
      <Button onClick={closeDialog}>Cancel</Button>
      <Button
        autoFocus
        highlight
        isLoading={form.isLoading}
        type="submit"
        onClick={form.willSubmitForm}
      >Assign case</Button>
    </>}
  >
    <p>Please review the details below:</p>
    <CaseRequestCard caseRequest={caseRequest}/>
    <p>Assigning case to:</p>
    <ExpertProfileCard expert={form.getValue("selectedExpert")}/>
    {!isSelfAssignment && <>
      <ToggleCheckbox
        label={<strong>Notify expert that the case was assigned</strong>}
        path="sendEmail"
      />
      <FormValue path="sendEmail">{sendEmail => sendEmail && <>
        <TextInput
          path="message"
          label="Message"
          descText="This will be included in the email sent to the expert."
          textarea
          autoResize
        />
        <SecurityDataWarning>
          <h4>This email may contain confidential and protected health care information.</h4>
            <p>
              Please be sure that the email of the recipient has been entered
              correctly and that you are using appropriately confidential mechanisms
              for this communication.
            </p>
        </SecurityDataWarning>
      </>}</FormValue>
    </>}

    <PendingInfo caseRequest={caseRequest} hideServiceTerms={!providerInfo.serviceTerms || !providerInfo.requiresAgreement}/>
    <Errors/>
  </Dialog>
}

const ExpertRow = ({selected, expert, setSelectedExpert, highlight}) => {
  const expertName = expert.firstName + " " + expert.lastName
 
  return <StyledExpertRow selected={selected} onClick={() => setSelectedExpert(expert)} highlight={highlight} title={highlight ? 'Selfassignment' : ''}>
    <div>
      <div>
        {expertName.length > 2 ?
          <><b>{expertName}</b> ({expert.expertEmail})</>
          :
          expert.expertEmail
        }</div>
      <IfLocale not contains="LEGAL">
        {expert.specialization?.length > 0 &&
          <div className="speciality">Speciality: {(expert.specialization || []).join(", ")}</div>
        }</IfLocale>
    </div>
    {expert.reviewerType === REVIEWER_TYPE_ADMIN && <AdminBadge>
      Administrator
    </AdminBadge>}
    <div className="check" css="position:relative">
      <ExpertRadioBox selected={selected} path="selectedExpert" fieldValue={expert}/>
      {selected && <Icon icon="check"/>}
    </div>
  </StyledExpertRow>
}
